import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { IconCamera, IconLoader2, IconTrash } from "@tabler/icons-react";

export function CheckThumbnail({ src, alt, onClick }: { src: string; alt: string; onClick: () => void }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <button onClick={onClick} className="shrink-0 self-start relative w-14 sm:w-16">
      {!loaded && <div className="absolute inset-0 rounded-md bg-muted animate-pulse" />}
      <img
        src={src}
        alt={alt}
        onLoad={() => setLoaded(true)}
        className={`h-full w-full rounded-md object-cover ring-1 ring-border aspect-[3/4] transition-opacity ${loaded ? "opacity-100" : "opacity-0"}`}
      />
    </button>
  );
}

export function InlineDeleteButton({
  onConfirm,
  disabled,
}: {
  onConfirm: () => void;
  disabled: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  const handleClick = () => {
    if (confirming) {
      clearTimeout(timerRef.current);
      setConfirming(false);
      onConfirm();
    } else {
      setConfirming(true);
      timerRef.current = setTimeout(() => setConfirming(false), 3000);
    }
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`h-6 p-0 rounded-full transition-all ${
            confirming
              ? "w-auto px-2 text-red-500 bg-red-500/10"
              : "w-6 text-muted-foreground/40 hover:text-red-500"
          }`}
          onClick={handleClick}
          disabled={disabled}
        >
          {confirming ? (
            <span className="text-[11px] font-medium">Delete?</span>
          ) : (
            <IconTrash className="h-3.5 w-3.5" />
          )}
        </Button>
      </TooltipTrigger>
      {!confirming && <TooltipContent side="bottom">Delete</TooltipContent>}
    </Tooltip>
  );
}

export function CheckPhotoSlot({
  thumbnailUrl,
  alt,
  isUploading,
  canEdit,
  onPhotoClick,
}: {
  thumbnailUrl?: string | null;
  alt: string;
  isUploading?: boolean;
  canEdit: boolean;
  onPhotoClick: () => void;
}) {
  if (thumbnailUrl) {
    return <CheckThumbnail src={thumbnailUrl} alt={alt} onClick={onPhotoClick} />;
  }
  if (isUploading) {
    return (
      <div className="shrink-0 self-start w-14 sm:w-16 aspect-[3/4] rounded-md border border-border flex items-center justify-center">
        <IconLoader2 className="h-5 w-5 animate-spin text-muted-foreground/50" />
      </div>
    );
  }
  if (canEdit) {
    return (
      <button
        onClick={onPhotoClick}
        className="shrink-0 self-start w-14 sm:w-16 aspect-[3/4] rounded-md border border-dashed border-border flex items-center justify-center text-muted-foreground/40 hover:text-muted-foreground/70 hover:border-muted-foreground/40 transition-colors"
      >
        <IconCamera className="h-5 w-5" />
      </button>
    );
  }
  return null;
}
